import { useId, useState } from "react";
import {
  Alert,
  Checkbox,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import type { Promisable } from "type-fest";

import {
  instrumentVersionLabel,
  isInTheLineageOf,
  isWithinPrefix,
  manifest,
  newestWithin,
  parseInstrumentName,
  renderInstrumentName,
} from "@thalamos/haven-common";

import { CancelOrProceedActions } from "../CancelOrProceedActions/CancelOrProceedActions.js";

const CALENDAR_DATE = new Intl.DateTimeFormat("en-GB", { dateStyle: "long" });

/**
 * Whether an assessment was started. One `failed` for every way it was not:
 * nothing the person in front of the dialog could do differently tells a
 * refusal from an unreachable server.
 */
export type StartOutcome = "started" | "failed";

/**
 * The finished assessment a new one would follow and could take ratings from,
 * as the dialog describes it.
 */
export type PreviousAssessment = {
  /** The instrument revision it was pinned to, as its name renders. */
  instrument: string;
  status: "approved" | "completed";
  /** When it was approved, or completed where it needed no approval. */
  finishedAt: Date;
  /** Whose it was; undefined where it was the starter's own. */
  assessorName: string | undefined;
  /** Who approved it; undefined for one that needed no approval. */
  approverName: string | undefined;
};

/** Where in the manifest a start in `version` pins, which orders versions by publication. */
const publishedAt = (version: string): number => {
  const pinned = newestWithin(manifest, version);

  return pinned === undefined ? -1 : manifest.indexOf(pinned);
};

/**
 * Whether ratings can carry over from `previous` into a start in `version`:
 * only on the same instrument, at that version or a later one.
 */
const carriesOverInto = (previous: PreviousAssessment, version: string) => {
  const pinned = newestWithin(manifest, version);

  return (
    pinned !== undefined &&
    isInTheLineageOf(
      manifest,
      previous.instrument,
      renderInstrumentName(pinned.module.name),
    )
  );
};

/** The newest published revision under `version`, as its name renders. */
const pinnedName = (version: string): string | undefined => {
  const pinned = newestWithin(manifest, version);

  return pinned === undefined
    ? undefined
    : renderInstrumentName(pinned.module.name);
};

/**
 * The version furthest along the instrument's lineage: the one every other
 * follows into, or, where no single one does, the one published last.
 */
const furthestOn = (versions: readonly string[]): string | undefined => {
  const leading = versions.filter((candidate) => {
    const pinned = pinnedName(candidate);

    return (
      pinned !== undefined &&
      versions.every((other) => {
        const otherPinned = pinnedName(other);

        return (
          otherPinned !== undefined &&
          isInTheLineageOf(manifest, otherPinned, pinned)
        );
      })
    );
  });

  return (leading.length > 0 ? leading : versions).reduce<string | undefined>(
    (newest, each) =>
      newest === undefined || publishedAt(each) > publishedAt(newest)
        ? each
        : newest,
    undefined,
  );
};

/**
 * Whether `version` is an earlier version of the instrument `previous` was on,
 * which its ratings cannot carry back into.
 */
const beforePrevious = (previous: PreviousAssessment, version: string) => {
  const pinned = pinnedName(version);

  return (
    pinned !== undefined &&
    isInTheLineageOf(manifest, pinned, previous.instrument)
  );
};

/** Whether `previous` was on `version` itself rather than an earlier one. */
const onVersion = (previous: PreviousAssessment, version: string) => {
  const name = parseInstrumentName(previous.instrument);
  const prefix = parseInstrumentName(version);

  return (
    name.success && prefix.success && isWithinPrefix(name.data, prefix.data)
  );
};

/** What a practitioner calls an offered version, by the revision a start in it pins. */
const offeredVersionLabel = (version: string): string => {
  const pinned = pinnedName(version);

  return pinned === undefined ? version : instrumentVersionLabel(pinned);
};

/** Present when the assessment is the definitive one. */
export type DefinitiveStart = {
  /** Undefined where no Approver is in place. */
  approverName: string | undefined;
};

/**
 * Asks which instrument version to assess in, and starts the assessment.
 *
 * Names the young person. A definitive start also says so and who will
 * approve it, or that nobody is in place to; any other start says neither.
 *
 * The versions are those the acting body may use; the newest revision of the
 * one chosen is what the assessment is pinned to. It stays open once started,
 * for the caller to move on from.
 *
 * Given a previous assessment, it preselects the newest version offered that
 * the assessment's ratings carry into; on that assessment's version or a
 * later one of its instrument it offers to carry the ratings over, ticked by
 * default, saying where the earlier assessment was on an earlier version, and
 * on any other version it warns that nothing will carry over. Without one it
 * is the plain start.
 */
export const StartAssessmentDialog = ({
  open,
  personName,
  definitive,
  versions,
  versionsFailed = false,
  previous,
  onClose,
  onStart,
}: {
  open: boolean;
  personName: string;
  definitive?: DefinitiveStart | undefined;
  /** Undefined until they have been read, which is not the same as none. */
  versions: readonly string[] | undefined;
  /** Whether reading them failed, so they will not arrive by waiting. */
  versionsFailed?: boolean;
  /** The assessment this one would follow, where ratings could carry over from one. */
  previous?: PreviousAssessment | undefined;
  onClose: () => Promisable<void>;
  /** Starts it, carrying the previous assessment's ratings over where `carryOver`. */
  onStart: (version: string, carryOver: boolean) => Promisable<StartOutcome>;
}) => {
  const { t } = useTranslation();
  const fromId = useId();
  const [chosen, setChosen] = useState<string>();
  const [carryOver, setCarryOver] = useState(true);
  const [status, setStatus] = useState<"starting" | "failed">();

  // The newest version offered of the earlier assessment's instrument: where
  // its ratings carry furthest forward.
  const carryOverVersion =
    previous === undefined
      ? undefined
      : furthestOn(
          versions?.filter((each) => carriesOverInto(previous, each)) ?? [],
        );
  // A choice no longer on offer counts for nothing, a lone version is chosen
  // already, and otherwise a start begins where the previous ratings carry
  // furthest.
  const version =
    chosen !== undefined && versions?.includes(chosen) === true
      ? chosen
      : versions?.length === 1
        ? versions[0]
        : carryOverVersion;
  const carriesOver =
    previous !== undefined &&
    version !== undefined &&
    carriesOverInto(previous, version);
  const starting = status === "starting";

  const close = () => {
    setChosen(undefined);
    setCarryOver(true);
    setStatus(undefined);
    void onClose();
  };

  const start = async () => {
    if (version === undefined) {
      return;
    }

    setStatus("starting");
    let outcome: StartOutcome;

    try {
      outcome = await onStart(version, carriesOver && carryOver);
    } catch {
      outcome = "failed";
    }

    if (outcome === "failed") {
      setStatus("failed");
      return;
    }

    setChosen(undefined);
    setCarryOver(true);
    setStatus(undefined);
  };

  return (
    <Dialog open={open} onClose={starting ? undefined : close} fullWidth>
      <DialogTitle>{t("personPage.assessments.startDialog.title")}</DialogTitle>
      <DialogContent>
        <DialogContentText>
          {t("personPage.assessments.startDialog.body", {
            name: personName,
          })}
        </DialogContentText>
        {definitive === undefined ? null : (
          <>
            <DialogContentText>
              {t("personPage.assessments.startDialog.definitive")}
            </DialogContentText>
            <DialogContentText>
              {definitive.approverName === undefined
                ? t("personPage.assessments.startDialog.noApprover")
                : t("personPage.assessments.startDialog.approver", {
                    approver: definitive.approverName,
                  })}
            </DialogContentText>
          </>
        )}
        {versionsFailed ? (
          <Alert severity="error">
            {t("personPage.assessments.startDialog.versionsFailed")}
          </Alert>
        ) : versions === undefined ? (
          <Typography variant="body2" color="text.secondary">
            {t("common.loading")}
          </Typography>
        ) : versions.length === 0 ? (
          <DialogContentText>
            {t("personPage.assessments.startDialog.none")}
          </DialogContentText>
        ) : (
          <>
            <TextField
              select
              fullWidth
              margin="normal"
              label={t(
                previous === undefined
                  ? "personPage.assessments.startDialog.version"
                  : "personPage.assessments.startDialog.instrument",
              )}
              helperText={
                carryOverVersion === undefined
                  ? undefined
                  : t("personPage.assessments.startDialog.instrumentHint", {
                      name: personName,
                    })
              }
              value={version ?? ""}
              onChange={(event) => {
                setChosen(event.target.value);
              }}
            >
              {versions.map((each) => (
                <MenuItem key={each} value={each}>
                  {offeredVersionLabel(each)}
                </MenuItem>
              ))}
            </TextField>
            {previous === undefined ||
            version === undefined ? null : carriesOver ? (
              <>
                <FormControlLabel
                  control={
                    <Checkbox
                      slotProps={{ input: { "aria-describedby": fromId } }}
                      checked={carryOver}
                      onChange={(event) => {
                        setCarryOver(event.target.checked);
                      }}
                    />
                  }
                  label={t("personPage.assessments.startDialog.carryOver")}
                />
                <DialogContentText id={fromId}>
                  {[
                    previous.assessorName === undefined
                      ? t("personPage.assessments.startDialog.fromYours", {
                          date: CALENDAR_DATE.format(previous.finishedAt),
                        })
                      : t("personPage.assessments.startDialog.fromTheirs", {
                          assessor: previous.assessorName,
                          date: CALENDAR_DATE.format(previous.finishedAt),
                        }),
                    ...(previous.approverName === undefined
                      ? []
                      : [
                          t("personPage.assessments.startDialog.approvedBy", {
                            approver: previous.approverName,
                          }),
                        ]),
                  ].join(" ")}
                </DialogContentText>
                {onVersion(previous, version) ? null : (
                  <Alert severity="info">
                    {t("personPage.assessments.startDialog.earlierVersion", {
                      version: instrumentVersionLabel(previous.instrument),
                    })}
                  </Alert>
                )}
              </>
            ) : (
              <Alert severity="warning">
                {t(
                  beforePrevious(previous, version)
                    ? "personPage.assessments.startDialog.noCarryOverBack"
                    : "personPage.assessments.startDialog.noCarryOver",
                  {
                    name: personName,
                    finished: t(
                      `personPage.assessments.startDialog.finished.${previous.status}`,
                    ),
                    date: CALENDAR_DATE.format(previous.finishedAt),
                    instrument: instrumentVersionLabel(previous.instrument),
                  },
                )}
              </Alert>
            )}
          </>
        )}
        {status === "failed" ? (
          <Alert severity="error">
            {t("personPage.assessments.startDialog.failed")}
          </Alert>
        ) : null}
      </DialogContent>
      <CancelOrProceedActions
        cancel={{
          onClick: close,
          disabled: starting,
        }}
        proceed={{
          label: t("personPage.assessments.startDialog.start"),
          onClick: start,
          disabled: version === undefined || starting,
          kind: "primary",
        }}
      />
    </Dialog>
  );
};
